/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import {
	ISchemaFlowNodeListModel,
	ISchemaFlowNodeModel,
	ISchemaFlowNodeTypeDefinitionMap,
	ISelectionItem_Base,
	Selection
} from "@adapptio/lib-blueprint";
import { createEventEmitter, emitEvent } from "@adapptio/lib-shared";
import { TViewEditSelectionItem } from "../../ViewEditor/Common/ViewEditSelection";

export enum ENDPOINT_SELECTION_ITEM_TYPE {
	FLOW_NODE = "flowNode"
}

export interface ISelectionItem_FlowNode extends ISelectionItem_Base {
	/** Item type */
	type: ENDPOINT_SELECTION_ITEM_TYPE.FLOW_NODE;
	nodeListModelId: number;
}

export type TEndpointEditSelectionItem = ISelectionItem_FlowNode;

/**
 * Type guard for flow node selection
 * @param value
 */
export function isSelectionItemEndpointFlowNode(
	value: TViewEditSelectionItem
): value is ISelectionItem_FlowNode {
	return value?.type === ENDPOINT_SELECTION_ITEM_TYPE.FLOW_NODE;
}

/**
 * Class to manage action model selections
 */
export class EndpointEditorEditSelection extends Selection<TEndpointEditSelectionItem> {
	/** When there's a try to select already selected item */
	public onSelectSelected = createEventEmitter<void>();

	/**
	 * Returns if flow node is selected
	 *
	 * @param nodeModel Flow Node model
	 */
	public isFlowNodeSelected(nodeModel: ISchemaFlowNodeModel<ISchemaFlowNodeTypeDefinitionMap>): boolean {
		if (!nodeModel) {
			return false;
		}

		const index = this.findItemIndexByNodeId(nodeModel.nodeId);
		return index >= 0 && this.items[index].type === ENDPOINT_SELECTION_ITEM_TYPE.FLOW_NODE;
	}

	/**
	 * Selects a flow node
	 * Return if the flow node was (de)selected or not
	 *
	 * @param nodeModel Flow Node model
	 */
	public selectFlowNode(
		nodeList: ISchemaFlowNodeListModel<ISchemaFlowNodeTypeDefinitionMap>,
		nodeModel: ISchemaFlowNodeModel<ISchemaFlowNodeTypeDefinitionMap>
	): boolean {
		if (!nodeList || !nodeModel) {
			return false;
		}

		const isSelected = this.isFlowNodeSelected(nodeModel);

		// In multiselect, de-selected the component, otherwise it's already selected so do nothing
		if (isSelected && this.multiSelect) {
			this.deselectFlowNode(nodeModel);
			return true;
		} else if (isSelected) {
			emitEvent(this.onSelectSelected);
			return false;
		}

		const selItem: ISelectionItem_FlowNode = {
			type: ENDPOINT_SELECTION_ITEM_TYPE.FLOW_NODE,
			nodeId: nodeModel.nodeId,
			nodeListModelId: nodeList.nodeId
		};

		if (this.multiSelect) {
			this.items.push(selItem);
		} else {
			this.unselectAll();
			this.items = [ selItem ];
		}

		emitEvent(this.onSelectSingle, selItem);
		emitEvent(this.onChange);

		return true;
	}

	/**
	 * Deselected the data source (if was selected) and returns if selection changes
	 *
	 * @param dsModel DataSource model
	 */
	public deselectFlowNode(nodeModel: ISchemaFlowNodeModel<ISchemaFlowNodeTypeDefinitionMap>): boolean {
		if (!nodeModel) {
			return false;
		}

		return this.deselectByNodeId(nodeModel.nodeId);
	}

	/**
	 * Sets the selection of flow nodes (replaces current selection entirely)
	 *
	 * @param nodeModels Flow node models to select
	 */
	public setFlowNodeSelection(
		nodeList: ISchemaFlowNodeListModel<ISchemaFlowNodeTypeDefinitionMap>,
		nodeModels: Array<ISchemaFlowNodeModel<ISchemaFlowNodeTypeDefinitionMap>>
	): void {
		// De-select previous items if not present in a new array
		const newIdList = nodeModels.map((item) => item.nodeId);
		const oldIdList = this.items.map((item) => item.nodeId);

		for (let i = 0; i < this.items.length; i++) {
			if (!newIdList.includes(this.items[i].nodeId)) {
				emitEvent(this.onUnselectSingle, this.items[i]);
			}
		}

		// Set new selection
		this.items = nodeModels.map((item) => ({
			type: ENDPOINT_SELECTION_ITEM_TYPE.FLOW_NODE,
			nodeId: item.nodeId,
			nodeListModelId: nodeList.nodeId
		}));

		// Emit events for newly selected items
		this.items.forEach((item) => {
			if (!oldIdList.includes(item.nodeId)) {
				emitEvent(this.onSelectSingle, item);
			} else {
				emitEvent(this.onSelectSelected);
			}
		});

		if (this.items.length === 0) {
			emitEvent(this.onUnselectAll);
		}

		emitEvent(this.onChange);
	}

	/**
	 * Deselected all
	 */
	public deselectAll() {
		this.unselectAll();
		emitEvent(this.onChange);
	}
}
